import Header from '../../components/public/Header';
import Footer from '../../components/public/Footer';
import { Link } from 'react-router-dom';
import { Users, Phone, Mail, ArrowRight } from 'lucide-react';

const Section = ({ title, children }) => (
  <div>
    <h2 className="font-heading text-xl font-semibold text-text-primary mb-3">{title}</h2>
    <div className="text-text-muted leading-relaxed space-y-3">{children}</div>
    <hr className="border-[#E4E8EB] mt-6" />
  </div>
);

const characteristics = [
  { name: 'Age', desc: 'Protection from discrimination based on how old you are.' },
  { name: 'Disability', desc: 'Physical, sensory, mental health, learning disability or long-term health condition.' },
  { name: 'Gender reassignment', desc: 'Proposing to, currently undergoing, or having undergone a process to change sex.' },
  { name: 'Marriage and civil partnership', desc: 'In the context of employment.' },
  { name: 'Pregnancy and maternity', desc: 'During pregnancy and for a period after giving birth.' },
  { name: 'Race', desc: 'Colour, nationality, ethnic or national origin.' },
  { name: 'Religion or belief', desc: 'Any religion or philosophical belief, or lack of belief.' },
  { name: 'Sex', desc: 'Whether you are a man or a woman.' },
  { name: 'Sexual orientation', desc: 'Whether you are heterosexual, gay, lesbian, or bisexual.' },
];

export default function EqualityPage() {
  return (
    <div className="min-h-screen bg-[#F8FAFA]">
      <Header />

      {/* Hero */}
      <section className="bg-white py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-accent rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Users className="h-8 w-8 text-primary" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-text-primary mb-4 leading-tight">
            Equality, Diversity &amp; Inclusion
          </h1>
          <p className="text-lg text-text-muted leading-relaxed">
            We are committed to treating every person with fairness, dignity and respect — whether they are a client, worker or visitor.
          </p>
          <p className="text-sm text-text-muted mt-4">Last updated: June 2026</p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl border border-[#E4E8EB] p-8 space-y-6">

            <Section title="Our commitment">
              <p>
                Osabea Healthcare Solutions Limited is committed to promoting equality, diversity and inclusion in everything we do. We believe that every person deserves to be treated with dignity and respect regardless of any protected characteristic.
              </p>
              <p>
                This statement is made in accordance with the <strong>Equality Act 2010</strong> and CQC guidance on person-centred care, non-discrimination, and equal treatment.
              </p>
            </Section>

            <Section title="The Equality Act 2010">
              <p>
                The Equality Act 2010 provides a legal framework to protect individuals from unfair treatment. It applies to the services we provide to clients, our employment practices, and our dealings with third parties.
              </p>
              <p>
                The Act protects people from discrimination, harassment and victimisation based on nine <strong>protected characteristics</strong>:
              </p>
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                {characteristics.map((c) => (
                  <div key={c.name} className="bg-[#F8FAFA] rounded-xl p-4 border border-[#E4E8EB]">
                    <p className="font-semibold text-text-primary text-sm">{c.name}</p>
                    <p className="text-xs text-text-muted mt-0.5">{c.desc}</p>
                  </div>
                ))}
              </div>
            </Section>

            <Section title="How we uphold equality in our services">
              <ul className="list-disc list-inside space-y-1 mt-1">
                <li>We provide care that is tailored to the individual, respecting their background, culture, language, religion, and personal preferences.</li>
                <li>We support clients to maintain their cultural and spiritual needs as part of their care plan.</li>
                <li>We make reasonable adjustments to how we deliver services to ensure they are accessible to people with disabilities.</li>
                <li>We provide communication support (e.g. translated materials, easy-read documents) where needed.</li>
                <li>We challenge and report any discriminatory behaviour by workers or others towards people in our care.</li>
              </ul>
            </Section>

            <Section title="How we uphold equality in employment">
              <ul className="list-disc list-inside space-y-1 mt-1">
                <li>We recruit, promote and treat all staff fairly, based on merit and ability, free from discrimination.</li>
                <li>We provide equal access to training, development and progression opportunities.</li>
                <li>We have a zero tolerance approach to bullying, harassment and discrimination in the workplace.</li>
                <li>We make reasonable adjustments for employees and workers with disabilities.</li>
                <li>We actively promote an inclusive workplace culture that values diversity.</li>
              </ul>
            </Section>

            <Section title="Types of discrimination we do not tolerate">
              <ul className="list-disc list-inside space-y-1 mt-1">
                <li><strong>Direct discrimination:</strong> Treating someone less favourably because of a protected characteristic.</li>
                <li><strong>Indirect discrimination:</strong> Applying a provision, criterion or practice that disadvantages people with a protected characteristic without justification.</li>
                <li><strong>Harassment:</strong> Unwanted conduct related to a protected characteristic that violates dignity or creates an intimidating, hostile, degrading, humiliating or offensive environment.</li>
                <li><strong>Victimisation:</strong> Treating someone badly because they have made or supported a complaint under the Equality Act.</li>
              </ul>
            </Section>

            <Section title="How to raise a concern">
              <p>
                If you believe you or someone else has experienced discrimination, harassment or unfair treatment in connection with our services or employment practices, you can raise a concern:
              </p>
              <ul className="list-disc list-inside space-y-1 mt-1">
                <li>Through our <Link to="/complaints" className="text-primary underline">Complaints Procedure</Link></li>
                <li>By contacting us directly by phone or email (see below)</li>
                <li>By contacting the <strong>Equality and Human Rights Commission (EHRC)</strong>: <a href="https://www.equalityhumanrights.com" target="_blank" rel="noopener noreferrer" className="text-primary underline">equalityhumanrights.com</a> | 0808 800 0082</li>
              </ul>
              <p>
                We take all equality concerns seriously and will investigate promptly and fairly. No one will be penalised for raising a genuine equality concern.
              </p>
            </Section>

            <Section title="Monitoring and review">
              <p>
                We review this policy and our equality practices regularly to ensure they remain effective and up to date with changes in the law and best practice. We will take action to address any equality issues identified through our monitoring processes.
              </p>
              <p>
                All staff and workers are required to complete equality and diversity training as part of their mandatory training requirements.
              </p>
            </Section>

          </div>

          {/* Contact */}
          <div className="bg-white rounded-2xl border border-[#E4E8EB] p-8 mt-6">
            <h2 className="font-heading text-xl font-semibold text-text-primary mb-4">Get in touch</h2>
            <div className="space-y-3">
              <a href="tel:01622809469" className="flex items-center gap-4 rounded-xl border border-[#E4E8EB] p-4 bg-[#F8FAFA] hover:border-primary/40 transition-colors">
                <Phone className="h-5 w-5 text-primary flex-shrink-0" />
                <div><div className="text-xs text-text-muted">Telephone</div><div className="font-medium">01622 809469</div></div>
              </a>
              <a href="mailto:info@osabeacares.co.uk" className="flex items-center gap-4 rounded-xl border border-[#E4E8EB] p-4 bg-[#F8FAFA] hover:border-primary/40 transition-colors">
                <Mail className="h-5 w-5 text-primary flex-shrink-0" />
                <div><div className="text-xs text-text-muted">Email</div><div className="font-medium">info@osabeacares.co.uk</div></div>
              </a>
            </div>
            <div className="mt-6 pt-6 border-t border-[#E4E8EB] flex flex-col sm:flex-row gap-3">
              <Link to="/complaints" className="inline-flex items-center gap-2 text-sm text-primary font-medium hover:underline">
                Our Complaints Policy <ArrowRight className="h-3 w-3" />
              </Link>
              <a href="https://www.equalityhumanrights.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-primary font-medium hover:underline">
                Equality & Human Rights Commission <ArrowRight className="h-3 w-3" />
              </a>
            </div>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
}
